/**
 * favorites.js — Heart button on product cards + product detail pages.
 *
 * Storage: logged-in Shopify customers sync to the Petra backend;
 * guests use localStorage. On login, guest favorites are merged into
 * the backend record.
 *
 * Auto-injects into any element with [data-product-id] on the page.
 * Safe to call multiple times — scans and re-scans on DOM changes.
 */
(function () {
  const API_BASE = 'https://admin.petraaroma.com';
  const LS_KEY = 'petra_favorites_v1';
  const EVT_CHANGE = 'petra:favorites:changed';

  const cfg = window.PetraFavoritesCfg || {};
  const customerEmail = (cfg.customerEmail || '').trim().toLowerCase();
  const customerId = cfg.customerId ? String(cfg.customerId) : '';
  const isLoggedIn = !!customerEmail;

  // ---- State cache ----
  let favoriteIds = new Set(); // string product IDs
  let favoriteRecords = [];    // full objects (from server or localStorage)

  // ---- Local storage helpers ----
  function readLocal() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function writeLocal(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch (e) { /* quota */ }
  }

  // ---- Server helpers (only used if isLoggedIn) ----
  async function serverList() {
    try {
      const res = await fetch(`${API_BASE}/api/favorites-public/${encodeURIComponent(customerEmail)}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.favorites) ? data.favorites : [];
    } catch (e) { return []; }
  }
  async function serverAdd(product) {
    try {
      await fetch(`${API_BASE}/api/favorites-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: customerEmail, shopify_customer_id: customerId, ...product })
      });
    } catch (e) { /* network — silently fail, localStorage still holds it */ }
  }
  async function serverRemove(productId) {
    try {
      await fetch(`${API_BASE}/api/favorites-public`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: customerEmail, product_id: productId })
      });
    } catch (e) { /* */ }
  }
  async function serverMerge(localList) {
    try {
      const res = await fetch(`${API_BASE}/api/favorites-public/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: customerEmail, shopify_customer_id: customerId, favorites: localList })
      });
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data.favorites) ? data.favorites : null;
    } catch (e) { return null; }
  }

  // ---- Public API ----
  async function loadInitial() {
    if (isLoggedIn) {
      const local = readLocal();
      if (local.length) {
        const merged = await serverMerge(local);
        if (merged) {
          favoriteRecords = merged;
          writeLocal([]); // clear local after successful merge
        } else {
          favoriteRecords = await serverList();
        }
      } else {
        favoriteRecords = await serverList();
      }
    } else {
      favoriteRecords = readLocal();
    }
    favoriteIds = new Set(favoriteRecords.map(f => String(f.product_id)));
    window.dispatchEvent(new CustomEvent(EVT_CHANGE));
  }

  async function toggle(product) {
    const id = String(product.product_id);
    if (!id) return;
    if (favoriteIds.has(id)) {
      favoriteIds.delete(id);
      favoriteRecords = favoriteRecords.filter(f => String(f.product_id) !== id);
      if (isLoggedIn) serverRemove(id);
      else writeLocal(favoriteRecords);
    } else {
      favoriteIds.add(id);
      const record = {
        product_id: id,
        product_handle: product.product_handle || null,
        product_title: product.product_title || null,
        product_image: product.product_image || null,
        product_price_cents: product.product_price_cents || null,
        created_at: new Date().toISOString()
      };
      favoriteRecords = [record, ...favoriteRecords];
      if (isLoggedIn) serverAdd(record);
      else writeLocal(favoriteRecords);
    }
    window.dispatchEvent(new CustomEvent(EVT_CHANGE));
  }

  function isFavorited(productId) {
    return favoriteIds.has(String(productId));
  }

  function list() {
    return favoriteRecords.slice();
  }

  // ---- DOM injection ----
  function heartSVG(filled) {
    if (filled) {
      return '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M12 21s-7.5-4.5-10-10C.2 7.5 3 4 6.5 4c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3C21 4 23.8 7.5 22 11c-2.5 5.5-10 10-10 10z"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7.5-4.5-10-10C.2 7.5 3 4 6.5 4c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3C21 4 23.8 7.5 22 11c-2.5 5.5-10 10-10 10z"/></svg>';
  }

  // The theme uses manual lazy-loading on product cards: the real src/srcset
  // are stored in data-manual-src / data-manual-srcset, and the actual src
  // attribute is omitted until JS swaps them in. Favoriting a card before
  // its image is in-viewport would otherwise capture an empty URL.
  function imageFromEl(imgEl) {
    if (!imgEl) return null;
    let image = imgEl.currentSrc
      || imgEl.getAttribute('src')
      || imgEl.getAttribute('data-manual-src')
      || imgEl.getAttribute('data-src')
      || null;
    if (!image) {
      const ss = imgEl.getAttribute('data-manual-srcset') || imgEl.getAttribute('srcset');
      if (ss) {
        const first = (ss.split(',')[0] || '').trim();
        if (first) image = first.split(/\s+/)[0];
      }
    }
    if (image && image.startsWith('//')) image = 'https:' + image;
    return image || null;
  }

  function extractProductFromCard(el) {
    const id = el.getAttribute('data-product-id') || '';
    const link = el.querySelector('a.product-link, a[href*="/products/"]');
    let handle = null;
    if (link && link.href) {
      const m = link.href.match(/\/products\/([^/?#]+)/);
      if (m) handle = m[1];
    }
    const titleEl = el.querySelector('.product-block__title, .product-title');
    return {
      product_id: id,
      product_handle: handle,
      product_title: titleEl ? titleEl.textContent.trim() : null,
      product_image: imageFromEl(el.querySelector('img'))
    };
  }

  function makeButton(product, variant) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'petra-fav-btn' + (variant ? ' petra-fav-btn--' + variant : '');
    btn.setAttribute('aria-label', 'Add to favorites');
    btn.setAttribute('data-petra-fav', product.product_id);
    btn.innerHTML = heartSVG(isFavorited(product.product_id));
    if (isFavorited(product.product_id)) btn.classList.add('is-favorited');
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await toggle(product);
    });
    return btn;
  }

  function renderButton(btn) {
    const id = btn.getAttribute('data-petra-fav');
    const filled = isFavorited(id);
    btn.innerHTML = heartSVG(filled);
    btn.classList.toggle('is-favorited', filled);
    btn.setAttribute('aria-label', filled ? 'Remove from favorites' : 'Add to favorites');
  }

  function injectIntoCards(root) {
    const scope = root || document;
    const cards = scope.querySelectorAll('product-block[data-product-id], [data-product-card][data-product-id]');
    cards.forEach(card => {
      if (card.dataset.petraFavInjected === '1') return;
      const product = extractProductFromCard(card);
      if (!product.product_id) return;
      const wrap = document.createElement('div');
      wrap.className = 'petra-fav-wrap';
      wrap.appendChild(makeButton(product, 'card'));
      const host = card.querySelector('.image-cont') || card.querySelector('.block-inner') || card;
      host.style.position = host.style.position || 'relative';
      host.appendChild(wrap);
      card.dataset.petraFavInjected = '1';
    });
  }

  function injectIntoProductPage() {
    // Main product form carries data-product-id
    const form = document.querySelector('form.js-product-form[data-product-id]');
    if (!form) return;
    const id = form.getAttribute('data-product-id');
    if (!id) return;
    if (document.querySelector('.petra-fav-btn--detail[data-petra-fav="' + id + '"]')) return;

    const handleMeta = document.querySelector('link[rel="canonical"]');
    let handle = null;
    if (handleMeta && handleMeta.href) {
      const m = handleMeta.href.match(/\/products\/([^/?#]+)/);
      if (m) handle = m[1];
    }
    const title = (document.querySelector('.product-title, h1.product-title, h1')?.textContent || '').trim() || null;
    const imgEl = document.querySelector('.media-gallery img, .product-media img, .product__media img, .product-gallery img, .main-product__image img');
    const image = imageFromEl(imgEl);

    const product = { product_id: id, product_handle: handle, product_title: title, product_image: image };
    const btn = makeButton(product, 'detail');
    btn.setAttribute('aria-label', isFavorited(id) ? 'Saved to favorites' : 'Save to favorites');

    // Inject near the add-to-cart / buy buttons
    const host = form.querySelector('.product-form__buttons, .product-form__cart, .buy-buttons, .product-form') || form;
    host.appendChild(btn);
  }

  function refreshAllButtons() {
    document.querySelectorAll('.petra-fav-btn').forEach(renderButton);
  }

  function scanAll() {
    injectIntoCards(document);
    injectIntoProductPage();
  }

  // Re-scan on common storefront events + DOM mutations (collection filters, quick-buy modals, etc.)
  function installObserver() {
    let pending = false;
    const obs = new MutationObserver((muts) => {
      if (pending) return;
      let hasNew = false;
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length) { hasNew = true; break; }
      }
      if (!hasNew) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; scanAll(); });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // Favorites page renderer (if a container exists on the page)
  async function renderFavoritesPage() {
    const mount = document.querySelector('[data-petra-favorites-list]');
    if (!mount) return;

    function draw() {
      const items = list();
      if (!items.length) {
        mount.innerHTML = '<div class="petra-fav-empty"><p>You haven\'t favorited anything yet.</p><p><a href="/collections/all" class="btn">Browse products</a></p></div>';
        return;
      }
      mount.innerHTML = '<div class="petra-fav-grid">' + items.map(f => {
        const href = f.product_handle ? `/products/${f.product_handle}` : '#';
        const img = f.product_image ? `<img src="${f.product_image}" alt="${(f.product_title||'').replace(/"/g,'&quot;')}" loading="lazy">` : '';
        const title = f.product_title || 'Product';
        return `
          <div class="petra-fav-card" data-product-id="${f.product_id}">
            <a href="${href}" class="petra-fav-card__img">${img}</a>
            <a href="${href}" class="petra-fav-card__title">${title}</a>
            <button type="button" class="petra-fav-btn petra-fav-btn--card is-favorited" data-petra-fav="${f.product_id}" aria-label="Remove from favorites">${heartSVG(true)}</button>
          </div>`;
      }).join('') + '</div>';

      mount.querySelectorAll('.petra-fav-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const id = btn.getAttribute('data-petra-fav');
          const record = favoriteRecords.find(r => String(r.product_id) === String(id));
          if (record) await toggle(record);
          draw();
        });
      });
    }
    draw();
    window.addEventListener(EVT_CHANGE, draw);
  }

  // Sync button state across the page whenever favorites change
  window.addEventListener(EVT_CHANGE, refreshAllButtons);

  // Expose a minimal global for debugging / other scripts
  window.PetraFavorites = { toggle, isFavorited, list, loadInitial };

  async function init() {
    await loadInitial();
    scanAll();
    installObserver();
    renderFavoritesPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
